import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ICONS } from '@/lib/icons'
import { cn } from '@/lib/utils'
import { AddressVerificationState } from '@/lib/enums/AddressVerificationState'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type { ComponentProps, ReactElement } from 'react'
import type { AdminAddressVerificationState } from './admin-address-verification-labels'
import {
  getDeliveryAddressVerificationState,
  getDeliveryAddressVerificationTooltip,
} from './delivery-address-verification'

function getMobileAddressVerificationLabel(state: AdminAddressVerificationState): string {
  if (state.Value === AddressVerificationState.Verified.Value) {
    return 'Address verified'
  }

  if (state.Value === AddressVerificationState.ManuallyVerified.Value) {
    return 'Address manually verified'
  }

  return 'Verify address'
}

interface DeliveryAddressVerificationBadgeProps {
  deliveryAddress?: Pick<Address, 'verification_state'> | null
  className?: string
  mobileLabelClassName?: string
  showMobileLabel?: boolean
  tooltipSide?: ComponentProps<typeof TooltipContent>['side']
  quietVerified?: boolean
}

export function DeliveryAddressVerificationBadge({
  deliveryAddress,
  className = '',
  mobileLabelClassName = '',
  showMobileLabel = false,
  tooltipSide = 'top',
  quietVerified = false,
}: DeliveryAddressVerificationBadgeProps): ReactElement | null {
  const state = getDeliveryAddressVerificationState(deliveryAddress)
  const tooltipText = getDeliveryAddressVerificationTooltip(deliveryAddress)
  if (!state) return null

  const Icon = ICONS[state.Icon] ?? null
  const shouldQuietVerified =
    quietVerified && state.Value === AddressVerificationState.Verified.Value

  return (
    <Tooltip>
      <Badge
        asChild
        variant={shouldQuietVerified ? 'successGhost' : state.Variant}
        size={showMobileLabel ? 'statusResponsive' : 'statusIcon'}
        shape="control"
        className={cn('justify-center', className)}
      >
        <TooltipTrigger aria-label={tooltipText ?? undefined}>
          {Icon ? <Icon /> : null}
          {showMobileLabel ? (
            <span className={cn('text-xs font-medium lg:hidden', mobileLabelClassName)}>
              {getMobileAddressVerificationLabel(state)}
            </span>
          ) : null}
        </TooltipTrigger>
      </Badge>
      {/* The enum's brand variant is badge-only; null preserves the tooltip's unstyled body. */}
      <TooltipContent variant={state.Variant === 'brand' ? null : state.Variant} side={tooltipSide}>
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  )
}
